import { ComponentRef, Directive, OnInit, inject } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';

import { IFormElementBaseConfig } from '../../_shared/models/form/form-element-base.model';
import { IFormElementChildFilter } from '../../_shared/models/form/form-element-child-filter.model';
import { IFormElementChild } from '../../_shared/models/form/form-element-child.model';
import { FormService } from '../form.service';
import { DynamicElementDirective } from '../../_shared/directives/dynamic-element.directive';

@Directive({
    standalone: true,
    selector: '[siriusDynamicFormElement]',
})
export class DynamicFormElementDirective
    extends DynamicElementDirective<IFormElementBaseConfig>
    implements OnInit
{
    private _formService = inject(FormService);

    // The pool only holds references for later `destroy()` calls, so the
    // narrower `AnyDynamicElement` typing buys nothing and would force a cast
    // at every `createComponent()` call (whose declared return is
    // `ComponentRef<unknown>` in the parent class).
    private _childPool: { [key: string]: ComponentRef<unknown> } = {};

    get name(): string {
        return this.config().name;
    }

    get form(): FormGroup {
        return this.config().form;
    }

    get control(): AbstractControl | null {
        return this.form.get(this.name);
    }

    get children(): IFormElementChild[] {
        return this.config().dependentFormFieldComponents ?? [];
    }

    override ngOnInit(): void {
        super.ngOnInit();

        this.control?.valueChanges.subscribe((x: string) => this.onChanges(x));
    }

    createChildElement(formElement: IFormElementBaseConfig) {
        formElement.form = this.form;

        const control = this._formService.createControl(formElement.value, formElement.validation);

        this.form.addControl(formElement.name, control);

        const child = this.createComponent(formElement);
        this._childPool[formElement.name] = child;
    }

    destroyChildElement(formElement: IFormElementBaseConfig) {
        this.form.removeControl(formElement.name);
        this._childPool[formElement.name].destroy();

        delete this._childPool[formElement.name];
    }

    private onChanges(nextValue: string) {
        if (nextValue === this.form.value[this.name]) {
            return;
        }

        this.children?.forEach((x: IFormElementChild) => {
            const control = this.form.get(x.dependentFormField.name);
            const visible = this.isVisible(nextValue, x.filters[0]);

            if (control) {
                if (!visible) this.destroyChildElement(x.dependentFormField);
            } else {
                if (visible) this.createChildElement(x.dependentFormField);
            }
        });
    }

    private isVisible(value: string, filter: IFormElementChildFilter): boolean {
        switch (filter.operator) {
            case 'SET_ANY':
                return filter.strValues.includes(value);
            case 'IS_NOT_EMPTY':
                return true;
            case 'SET_NOT_ANY':
                return !filter.strValues.includes(value);
            default:
                return false;
        }
    }
}
